import type { OrderCategory, OrderLane } from "./mockAiService";

/**
 * Deterministic Plan → Doctor Orders parser.
 *
 * Turns the free text of the SOAP "Plan" slot (CARIVA's `plan` section, or what
 * the doctor typed) into structured draft orders. One order per line/bullet.
 * No AI, no network — so orders always reflect exactly what is in the Plan slot.
 * Bilingual: recognises common Thai and English clinical wording.
 */

export type PlanOrder = {
  category: OrderCategory;
  label: string;
  details: string;
  lane: OrderLane;
  stat: boolean;
};

// Leading list markers and clinical lead-in verbs to strip from a line.
const BULLET = /^[\s\-*•·▪◦]+|^\d+[.)]\s*/;
const LEAD_VERB =
  /^(?:ให้ยา|สั่งยา|สั่งตรวจ|สั่ง|ให้|prescribe|order|give|start|administer|continue|arrange|obtain|schedule|check|send|repeat)\s+/i;

const RE = {
  fluid: /\b(nss|saline|น้ำเกลือ|dextrose|d5|d5w|rls|ringer|iv fluid|colloid)\b/i,
  procedure:
    /\b(ecg|ekg|x-?ray|ultrasound|u\/s|ct\b|mri|scan|echo|endoscopy|transfusion|prc|เอกซเรย์|คลื่นไฟฟ้าหัวใจ|อัลตราซาวด์|ส่องกล้อง|ให้เลือด)\b/i,
  lab: /\b(cbc|lab|labs|culture|electrolyte|creatinine|bun|lft|urinalysis|ua\b|glucose|hba1c|inr|ptt|abg|ตรวจเลือด|ตรวจปัสสาวะ|เพาะเชื้อ|ตรวจทางห้องปฏิบัติการ)\b/i,
  medication:
    /\b(mg|mcg|µg|ml|g\b|tab|tablet|cap|capsule|dose|iv|im|po|sc|prn|bid|tid|qid|od\b|hs\b|ยา|paracetamol|ibuprofen|amox)/i,
  continuous: /\b(continue|daily|ต่อเนื่อง|ทุกวัน|bid|tid|qid|q\d+\s*h|q\d+h|ต่อ)\b/i,
  stat: /\b(stat|urgent|emergency|ด่วน|ทันที|เดี๋ยวนี้)\b/i,
  heading: /^#{1,6}\s|^(subjective|objective|assessment|plan)\s*[:：]/i,
};

function classify(text: string): OrderCategory {
  if (RE.fluid.test(text)) return "fluid";
  if (RE.procedure.test(text)) return "procedure";
  if (RE.lab.test(text)) return "lab";
  if (RE.medication.test(text)) return "medication";
  return "other";
}

/** Split a cleaned line into a short name (label) and the remaining detail. */
function splitNameDetail(line: string): { label: string; details: string } {
  // Name up to the first number (dose/time) becomes the label; the rest detail.
  const m = line.match(/^(.*?[^\s\d])\s+(\d.*)$/);
  if (m && m[1].trim().length >= 2) {
    return { label: m[1].trim(), details: m[2].trim() };
  }
  // Otherwise split on the first comma / bullet separator.
  const sep = line.search(/[,;·]/);
  if (sep > 1) {
    return { label: line.slice(0, sep).trim(), details: line.slice(sep + 1).trim() };
  }
  return { label: line.trim(), details: "" };
}

/**
 * Parse Plan text into draft orders. Returns [] when there is nothing orderable
 * (empty plan, headings only). Caller assigns id / source / attribution.
 */
export function parseOrdersFromPlan(plan: string | undefined | null): PlanOrder[] {
  if (!plan || !plan.trim()) return [];
  const orders: PlanOrder[] = [];
  const seen = new Set<string>();

  for (const raw of plan.split(/\r?\n|(?<=[.;•])\s+/)) {
    let line = raw.trim();
    if (!line || RE.heading.test(line)) continue;
    line = line.replace(BULLET, "").trim();
    if (!line) continue;
    const forClass = line;
    const stripped = line.replace(LEAD_VERB, "").trim() || line;
    const { label, details } = splitNameDetail(stripped);
    if (!label || label.length < 2) continue;

    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    orders.push({
      category: classify(forClass),
      label: label.replace(/[.\s]+$/u, "").trim(),
      details: details.replace(/[.]+$/, "").trim(),
      lane: RE.continuous.test(forClass) ? "continuous" : "one-day",
      stat: RE.stat.test(forClass),
    });
  }
  return orders;
}
